import { forwardRef, useState, type SVGProps, type CSSProperties } from "react";

export type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
  isHovered?: boolean;
};

const base = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function draw(isHovered?: boolean, delay = 0): CSSProperties {
  return {
    strokeDasharray: 1,
    strokeDashoffset: 0,
    animation: isHovered
      ? `icon-stroke-draw 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s both`
      : isHovered === false
      ? "none"
      : undefined,
  };
}

function useHoverState(
  externalHover?: boolean,
  onMouseEnter?: React.MouseEventHandler<SVGSVGElement>,
  onMouseLeave?: React.MouseEventHandler<SVGSVGElement>
) {
  const [internalHover, setInternalHover] = useState(false);
  const isHovered = externalHover !== undefined ? externalHover : internalHover;

  return {
    isHovered,
    onMouseEnter: (e: React.MouseEvent<SVGSVGElement>) => {
      setInternalHover(true);
      onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent<SVGSVGElement>) => {
      setInternalHover(false);
      onMouseLeave?.(e);
    },
  };
}

export const AnimatedDashboard = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <rect x="3" y="3" width="7" height="9" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <rect x="14" y="3" width="7" height="5" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.07)} />
        <rect x="14" y="12" width="7" height="9" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.14)} />
        <rect x="3" y="16" width="7" height="5" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.21)} />
      </svg>
    );
  }
);
AnimatedDashboard.displayName = "AnimatedDashboard";

export const AnimatedCalendar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <line x1="16" y1="2" x2="16" y2="6" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.08)} />
        <line x1="8" y1="2" x2="8" y2="6" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.14)} />
        <line x1="3" y1="10" x2="21" y2="10" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.2)} />
      </svg>
    );
  }
);
AnimatedCalendar.displayName = "AnimatedCalendar";

export const AnimatedDollar = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <line x1="12" y1="1" x2="12" y2="23" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.1)} />
      </svg>
    );
  }
);
AnimatedDollar.displayName = "AnimatedDollar";

export const AnimatedInbox = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.12)} />
      </svg>
    );
  }
);
AnimatedInbox.displayName = "AnimatedInbox";

export const AnimatedClipboard = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <rect x="8" y="2" width="8" height="4" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.08)} />
        <line x1="9" y1="12" x2="15" y2="12" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.15)} />
        <line x1="9" y1="16" x2="15" y2="16" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.22)} />
      </svg>
    );
  }
);
AnimatedClipboard.displayName = "AnimatedClipboard";

export const AnimatedSettings = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <circle cx="12" cy="12" r="3" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.1)} />
      </svg>
    );
  }
);
AnimatedSettings.displayName = "AnimatedSettings";

export const AnimatedUsers = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <circle cx="9" cy="7" r="4" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.08)} />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.16)} />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.22)} />
      </svg>
    );
  }
);
AnimatedUsers.displayName = "AnimatedUsers";

export const AnimatedOrgSettings = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M3 21h18" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <path d="M5 21V7l8-4v18" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.08)} />
        <path d="M19 21V11l-6-4" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.15)} />
        <path d="M9 9v.01" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.2)} />
        <path d="M9 12v.01" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.24)} />
        <path d="M9 15v.01" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.28)} />
        <path d="M9 18v.01" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.32)} />
      </svg>
    );
  }
);
AnimatedOrgSettings.displayName = "AnimatedOrgSettings";

export const AnimatedAllOrgs = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <rect x="1" y="6" width="9" height="14" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <rect x="14" y="6" width="9" height="14" rx="1" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.08)} />
        <path d="M5.5 10h3" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.15)} />
        <path d="M5.5 14h3" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.18)} />
        <path d="M18.5 10h3" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.21)} />
        <path d="M18.5 14h3" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.24)} />
      </svg>
    );
  }
);
AnimatedAllOrgs.displayName = "AnimatedAllOrgs";

export const AnimatedLogout = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <line x1="21" y1="12" x2="9" y2="12" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.1)} />
        <polyline points="16 17 21 12 16 7" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.16)} />
      </svg>
    );
  }
);
AnimatedLogout.displayName = "AnimatedLogout";

export const AnimatedShoppingBag = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 24, isHovered: externalHover, className, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const { isHovered, onMouseEnter: handleEnter, onMouseLeave: handleLeave } = useHoverState(externalHover, onMouseEnter, onMouseLeave);

    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" className={className} onMouseEnter={handleEnter} onMouseLeave={handleLeave} {...base} {...props}>
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0)} />
        <line x1="3" y1="6" x2="21" y2="6" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.1)} />
        <path d="M16 10a4 4 0 0 1-8 0" pathLength="1" className="animated-icon-path" style={draw(isHovered, 0.16)} />
      </svg>
    );
  }
);
AnimatedShoppingBag.displayName = "AnimatedShoppingBag";

